import { useMemo, useState } from "react";

import { faSearch, faTrashAlt } from "@fortawesome/pro-light-svg-icons";
import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { endOfToday, startOfToday, subDays } from "date-fns";
import { isEmpty } from "lodash-es";
import { DateRange, SelectRangeEventHandler } from "react-day-picker";
import { twJoin } from "tailwind-merge";

import { CollapsibleWidget } from "src/base-components/CollapsibleWidget";
import { DateRangePicker } from "src/base-components/DateRangePicker";
import { TableComp } from "src/base-components/Table";
import {
  EventFilterRequest,
  EventFilterRequestEnvironmentEnum,
} from "src/clients/features-retrieve";
import { EmptyState } from "src/design-system/EmptyState";
import { useSidepane } from "src/entities/entityView/utils";
import { useEntitySchemas } from "src/entities/queries";
import { getTableStoreKey } from "src/eventsCatalogue/EventsCatalogue";
import { EventsFilterDropdown } from "src/eventsCatalogue/EventsFilterDropdown";
import {
  useEventSchema,
  useEventSchemas,
  useEventsFilters,
  useFilteredEventsQuery,
} from "src/eventsCatalogue/queries";
import { getColumns } from "src/eventsCatalogue/tableConfig";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { WhitePane } from "src/jobs/common/WhitePane";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { EntityViewParams } from "src/router/urls";
import { errorMessage } from "src/utils/stringUtils";
import { useTimeWindow } from "src/utils/timeWindow";
import { useParamsDecode } from "src/utils/useParamsDecode";

const EventBlockContent: React.FC<{
  eventType: string;
  entityId: string;
  environment: EventFilterRequestEnvironmentEnum;
  filterParams: EventFilterRequest | null;
  hasFilterCondition: boolean;
  clearFilters: () => void;
}> = ({
  eventType,
  entityId,
  environment,
  filterParams,
  hasFilterCondition,
  clearFilters,
}) => {
  const { workspace } = useWorkspaceContext();

  const { data: schema, isLoading: isLoadingSchema } =
    useEventSchema(eventType);

  const {
    events,
    isPending: isFilterPending,
    error: filterError,
    isRefetching: isRefetchingEvents,
    isFetchingNextPage,
    handleFetchNextPage,
  } = useFilteredEventsQuery({
    eventType,
    environment,
    filterParams,
    entityId,
  });

  const { data: entitiesSchemasData } = useEntitySchemas({
    baseUrl: workspace.base_url!,
  });

  const { resourceId: selectedEventId, toggleSidepane } = useSidepane("event");

  const memoizedColumns = useMemo(() => {
    return schema ? getColumns(schema, environment, entitiesSchemasData) : [];
  }, [schema, entitiesSchemasData, environment]);

  const isLoading = isFilterPending || isLoadingSchema || isRefetchingEvents;

  const renderEventsContent = () => {
    // Show filter error if present (including 400 errors from POST request)
    if (filterError && (!isFilterPending || filterError.status === 400)) {
      const error = filterError.data
        ? errorMessage(filterError?.data)
        : "There was an error loading events. Please try again.";
      return (
        <EmptyState
          dataLoc={`${eventType}-filter-error-state`}
          description={error}
          headline="Error loading events"
          icon={faExclamationCircle}
          variant="error"
        />
      );
    }
    if (!isLoading && isEmpty(events)) {
      return (
        <EmptyState
          actionButton={
            hasFilterCondition
              ? {
                  label: "Clear filters",
                  onClick: clearFilters,
                  icon: faTrashAlt,
                }
              : undefined
          }
          dataLoc={`${eventType}-empty-state`}
          description={
            hasFilterCondition
              ? "No data found for the selected filters. Adjust the filters, and try running the query again."
              : "Try adjusting the filter to find what you are looking for"
          }
          headline="No events found"
          icon={faSearch}
        />
      );
    }

    return (
      <div className="relative flex h-[364px] w-full overflow-x-auto">
        <TableComp
          columns={memoizedColumns}
          data={events}
          dataLoc={`${eventType}-events-table`}
          frameClassName="w-full"
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          rowPropsGetter={(row) => ({
            onClick: () => {
              toggleSidepane({
                id: row.original.payload.id,
                eventType: eventType,
              });
            },
            classNameOverrides: twJoin(
              row.original.payload.id === selectedEventId
                ? "selected bg-gray-50"
                : "hover:bg-gray-50",
              "cursor-pointer",
            ),
          })}
          uiStoreKey={getTableStoreKey(eventType)}
          variant="compact"
          onScrolledToTheBottom={handleFetchNextPage}
        />
      </div>
    );
  };

  return (
    <div className="decideScrollbar overflow-hidden p-4 pt-3">
      {renderEventsContent()}
    </div>
  );
};

type EventBlockProps = {
  eventType: string;
  defaultOpen: boolean;
  entityId: string;
  displayName: string;
};

const EventBlock: React.FC<EventBlockProps> = ({
  displayName,
  eventType,
  defaultOpen,
  entityId,
}) => {
  const [environment] = useEnvironment();
  const { featuresEventsEnabled } = useWorkspaceFeatureGates();
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);

  const { dateRangePickerValue, onDateRangePickerChange, timeWindow } =
    useTimeWindow(
      {
        from: subDays(startOfToday(), 7),
        to: endOfToday(),
      },
      `events-time-window-${eventType}`,
    );

  const onTimeWindowChange: SelectRangeEventHandler = (
    range: DateRange | undefined,
  ) => {
    if (range?.from && range?.to) {
      onDateRangePickerChange(range);
    }
  };

  const {
    applyFilters,
    clearFilters,
    urlFilterParams,
    filterParams,
    hasFilterCondition,
  } = useEventsFilters({
    entityId,
    eventType,
    endTimestamp: timeWindow?.to?.toISOString() || "",
    environment: environment as EventFilterRequestEnvironmentEnum,
    startTimestamp: timeWindow?.from?.toISOString() || "",
  });

  return (
    <CollapsibleWidget
      rightSlot={
        <div
          className={twJoin(
            "flex h-full items-center border-gray-200 border-l",
            featuresEventsEnabled ? "" : "gap-x-2 pr-4",
          )}
        >
          <DateRangePicker
            disabled={isFilterDropdownOpen}
            suffixIcon={!isFilterDropdownOpen}
            usePredefinedDates={!isFilterDropdownOpen}
            value={dateRangePickerValue}
            borderless
            onChange={onTimeWindowChange}
          />
          {featuresEventsEnabled && (
            <EventsFilterDropdown
              dateRangeValue={timeWindow}
              eventType={eventType}
              hasFilterCondition={hasFilterCondition}
              isOpen={isFilterDropdownOpen}
              urlFilterParams={urlFilterParams}
              onApplyFilters={applyFilters}
              onClearFilters={clearFilters}
              onDateRangeChange={onTimeWindowChange}
              onOpenChange={setIsFilterDropdownOpen}
            />
          )}
        </div>
      }
      dataLoc={`${eventType}-group`}
      defaultOpen={defaultOpen}
      title={displayName}
    >
      <EventBlockContent
        entityId={entityId}
        eventType={eventType}
        environment={environment as EventFilterRequestEnvironmentEnum}
        filterParams={filterParams}
        hasFilterCondition={hasFilterCondition}
        clearFilters={clearFilters}
      />
    </CollapsibleWidget>
  );
};

type EntityEventsProps = {
  entityId: string;
};

export const EntityEvents: React.FC<EntityEventsProps> = ({ entityId }) => {
  const { data: eventTypes, isLoading: isLoadingEventTypes } =
    useEventSchemas();

  if (!eventTypes?.length && !isLoadingEventTypes) {
    return (
      <WhitePane noMinWidth>
        <EmptyState
          dataLoc="events-empty-state"
          description="There are no events found related to this entity yet."
          headline="No events found"
          icon={faSearch}
        />
      </WhitePane>
    );
  }

  return (
    <div className="flex flex-col gap-y-3">
      {eventTypes?.map((eventType, index) => (
        <EventBlock
          key={eventType.event_type}
          defaultOpen={index === 0}
          displayName={eventType.display_name_plural}
          entityId={entityId}
          eventType={eventType.event_type}
        />
      ))}
    </div>
  );
};

export const EventsTabContent = () => {
  const { id } = useParamsDecode<EntityViewParams>();

  return (
    <div className="p-5">
      <EntityEvents entityId={id} />
    </div>
  );
};
